import { DashboardTrainee } from '@common/types/person.types'
import Icon from '@components/dataDisplay/Icon'
import Chip from '@components/layout/Chip'
import { GREY_COLORS, PARKER_GOLDEN_COLORS } from '@config/theme/colors'
import { FontWeight } from '@config/theme/sizes'
import { LockSharp, WorkspacePremiumRounded } from '@mui/icons-material'
import { Stack, StackProps, styled, Typography } from '@mui/material'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import CertificationCreationPopIn from '../../../trainee/components/certifications/CertificationCreationPopIn'

type TraineeRowProps = {
	trainee: DashboardTrainee
	onCertificationCreated: () => void
}

/**
 * Trainee row
 * @param trainee
 * @param onCertificationCreated
 * @constructor
 */
const TraineeRow = (
	{
		trainee,
		onCertificationCreated
	}: TraineeRowProps
) => {
	const [isCertificationCreationPopInOpen, setIsCertificationCreationPopInOpen] = useState(false)
	const [allowRowClick, setAllowRowClick] = useState(true) // Because of propagation

	return (
		<Container
			{...(allowRowClick ? { to: `${trainee.id}` } : {})}
			component={allowRowClick ? Link : 'div'}
		>
			<Trainee>
				<FormattedMessage
					id="dashboard.trainees.list.trainee"
					values={{
						a: (text) => <TraineeCompanyText>{text}</TraineeCompanyText>,
						b: (text) => <Typography key="trainee" fontWeight={FontWeight.MEDIUM}>{text}</Typography>,
						c: (text) => <Typography key="company" variant="body2">{text}</Typography>,
						name: `${trainee.lastname} ${trainee.firstname}`,
						company: trainee.companyName
					}}
				/>
				<Chip color={PARKER_GOLDEN_COLORS}>
					<FormattedMessage id={`global.enums.companyType.${trainee.companyType}`} />
				</Chip>
			</Trainee>
			<InfoAndActions>
				<Actions className="actions">
					<Action
						onClick={(event) => {
							event.preventDefault()
							setAllowRowClick(false)
							setIsCertificationCreationPopInOpen(true)
						}}
					>
						<Typography variant="subtitle2">
							<FormattedMessage id="dashboard.trainees.list.actions.addCertification.label" />
						</Typography>
					</Action>
				</Actions>
				<Information className="certifications">
					{trainee.isTrainer && (
						<Chip
							color={GREY_COLORS}
							EndAdornment={!trainee.isActive ? LockSharp : undefined}
							endAdornmentSize="12px"
						>
							<FormattedMessage id="global.fields.trainer" />
						</Chip>
					)}
				</Information>
				<Certifications>
					<Icon IconElement={WorkspacePremiumRounded} size="20px" />
					<CertificationNumber>
						<Typography color={GREY_COLORS[500]} variant="subtitle2">
							{`${trainee.validCertificationsNumber} / ${trainee.certificationsNumber}`}
						</Typography>
					</CertificationNumber>
				</Certifications>
			</InfoAndActions>
			{(isCertificationCreationPopInOpen) && (
				<CertificationCreationPopIn
					traineeId={trainee.id}
					onClose={() => {
						setAllowRowClick(true)
						setIsCertificationCreationPopInOpen(false)
					}}
					onSuccess={() => {
						onCertificationCreated()
						setAllowRowClick(true)
						setIsCertificationCreationPopInOpen(false)
					}}
					isOpen
				/>
			)}
		</Container>
	)
}

const Container = styled(Stack)<StackProps>({
	color: 'inherit',
	flexDirection: 'row',
	justifyContent: 'space-between',
	padding: '8px 12px',
	textDecoration: 'none',
	'&:hover': {
		backgroundColor: GREY_COLORS[100],
		'& .certifications': {
			display: 'none'
		},
		'& .actions': {
			display: 'flex'
		}
	}
})

const Trainee = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const TraineeCompanyText = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const InfoAndActions = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row'
})

const Information = styled(Stack)({
	columnGap: '8px',
	flexDirection: 'row'
})

const Certifications = styled(Stack)({
	alignItems: 'center',
	border: `1px solid transparent`,
	borderRadius: '4px',
	columnGap: '4px',
	flexDirection: 'row',
	padding: '2px 8px'
})

const CertificationNumber = styled(Stack)({
	alignItems: 'flex-end',
	minWidth: '40px'
})

const Actions = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	display: 'none',
	flexDirection: 'row'
})

const Action = styled(Stack)({
	alignItems: 'center',
	border: `1px solid ${GREY_COLORS[400]}`,
	borderRadius: '8px',
	columnGap: '4px',
	cursor: 'pointer',
	flexDirection: 'row',
	padding: '4px 8px',
	backgroundColor: `${PARKER_GOLDEN_COLORS[500]}`
})

export default TraineeRow
